// Frontend-assisted session audit (Option B).
//
// Posts login-session lifecycle events to the backend for the audit trail.
// Fire-and-forget: failures are swallowed so auditing can never block or break
// the user's flow. Identity is derived server-side from the authenticated
// principal — we only send a correlation `session_key` (shared across tabs),
// an idempotency `event_id`, the event type, and optional metadata.

import { getToken } from './tokenStorage';
import { AUTH_STORAGE_KEYS } from './keys';
import { log } from '@/shared/logger/logger';
import { createStorage } from '@/shared/storage';

export type SessionEventType = 'login' | 'reauth' | 'activity' | 'expired' | 'logout';

let _storage: ReturnType<typeof createStorage> | null = null;
function storage() {
  if (_storage === null) _storage = createStorage('local');
  return _storage;
}

/** Test seam: drop the cached storage handle (mirrors shared/auth/session). */
export function __resetStorageForTests(): void {
  _storage = null;
}

function uuid(): string {
  // crypto.randomUUID is available in all supported browsers; guard for older
  // test environments.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Stable per-login correlation id, shared across tabs via localStorage. */
export function getOrCreateSessionKey(): string {
  const existing = storage().get<string>(AUTH_STORAGE_KEYS.sessionKey, '');
  if (existing) return existing;
  const key = uuid();
  storage().set(AUTH_STORAGE_KEYS.sessionKey, key);
  return key;
}

/** Drop the correlation id (e.g. on a different-user login). */
export function resetSessionKey(): void {
  storage().remove(AUTH_STORAGE_KEYS.sessionKey);
}

export interface RecordOptions {
  /** Absolute session expiry (epoch ms). */
  expiresAt?: number | null;
  detail?: Record<string, unknown>;
}

/** Server-authoritative session row echoed back by the audit endpoint. */
export interface SessionEventResult {
  /** Server-known login time (epoch ms). */
  startedAt: number | null;
  /** Server-known absolute expiry (epoch ms), or null if unset. */
  expiresAt: number | null;
}

function parseMs(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Record one session event. Never throws — audit must not break the app.
 *
 * Returns the server-authoritative `started_at`/`expires_at` from the
 * `user_sessions` row so the caller can anchor the absolute session clock to
 * the server-known login time (rather than a local guess). Returns null when
 * the post failed or the body could not be parsed.
 */
export async function recordSessionEvent(
  eventType: SessionEventType,
  opts: RecordOptions = {},
): Promise<SessionEventResult | null> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getToken();
    if (token !== null && token.length > 0) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const body: Record<string, unknown> = {
      session_key: getOrCreateSessionKey(),
      event_id: uuid(),
      event_type: eventType,
    };
    if (opts.expiresAt != null) body['expires_at'] = new Date(opts.expiresAt).toISOString();
    if (opts.detail) body['detail'] = opts.detail;

    const resp = await fetch('/api/v1/auth/session-events', {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as Record<string, unknown>;
    return {
      startedAt: parseMs(data['started_at']),
      expiresAt: parseMs(data['expires_at']),
    };
  } catch {
    // Swallow — audit is best-effort.
    log.warn('session_event_post_failed', { eventType });
    return null;
  }
}
